import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Slider,
  Box,
  Grid,
  ListSubheader
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

const FilterDialog = ({ open, onClose, filters, setFilters, onApplyFilters }) => {
  const handlePayoutRangeChange = (event, newValue) => {
    // Ensure values are numbers and within range
    const min = parseFloat(newValue[0]);
    const max = parseFloat(newValue[1]);
    
    setFilters({
      ...filters, 
      payoutRange: [
        isNaN(min) ? 0 : min,
        isNaN(max) ? 1 : max
      ]
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Advanced Filters
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Performance</InputLabel>
              <Select
                value={filters.performance}
                label="Performance"
                onChange={(e) => setFilters({...filters, performance: e.target.value})}
              >
                <MenuItem value="all">All Performance</MenuItem>
                <MenuItem value="high">High Payout (&gt;$0.50)</MenuItem>
                <MenuItem value="medium">Medium Payout ($0.25-$0.50)</MenuItem>
                <MenuItem value="low">Low Payout (&lt;$0.25)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography gutterBottom>Payout Range ($ per hit)</Typography>
            <Slider
              value={filters.payoutRange}
              onChange={handlePayoutRangeChange}
              valueLabelDisplay="auto"
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: '$0' },
                { value: 0.25, label: '$0.25' },
                { value: 0.5, label: '$0.50' },
                { value: 0.75, label: '$0.75' },
                { value: 1, label: '$1.00' }
              ]}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                <MenuItem value="all">All Categories</MenuItem>
                
                <ListSubheader>Business & Finance</ListSubheader>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="finance">Finance</MenuItem>
                <MenuItem value="investing">Investing</MenuItem>
                <MenuItem value="banking">Banking</MenuItem>
                <MenuItem value="insurance">Insurance</MenuItem>
                <MenuItem value="careers">Careers & Employment</MenuItem>
                
                <ListSubheader>Technology</ListSubheader>
                <MenuItem value="technology">Technology</MenuItem>
                <MenuItem value="computing">Computing</MenuItem>
                <MenuItem value="internet">Internet</MenuItem>
                <MenuItem value="software">Software</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="mobile">Mobile & Wireless</MenuItem>
                
                <ListSubheader>Health & Wellness</ListSubheader>
                <MenuItem value="health">Health</MenuItem>
                <MenuItem value="medicine">Medicine</MenuItem>
                <MenuItem value="fitness">Fitness</MenuItem>
                <MenuItem value="nutrition">Nutrition</MenuItem>
                <MenuItem value="mental_health">Mental Health</MenuItem>
                <MenuItem value="diseases">Diseases & Conditions</MenuItem>
                
                <ListSubheader>Lifestyle</ListSubheader>
                <MenuItem value="lifestyle">Lifestyle</MenuItem>
                <MenuItem value="fashion">Fashion</MenuItem>
                <MenuItem value="beauty">Beauty</MenuItem>
                <MenuItem value="food">Food & Dining</MenuItem>
                <MenuItem value="home">Home & Garden</MenuItem>
                <MenuItem value="parenting">Parenting</MenuItem>
                <MenuItem value="relationships">Relationships</MenuItem>
                
                <ListSubheader>Entertainment</ListSubheader>
                <MenuItem value="entertainment">Entertainment</MenuItem>
                <MenuItem value="movies">Movies</MenuItem>
                <MenuItem value="music">Music</MenuItem>
                <MenuItem value="television">Television</MenuItem>
                <MenuItem value="books">Books & Literature</MenuItem>
                <MenuItem value="gaming">Gaming</MenuItem>
                <MenuItem value="celebrities">Celebrities</MenuItem>
                
                <ListSubheader>Travel & Transportation</ListSubheader>
                <MenuItem value="travel">Travel</MenuItem>
                <MenuItem value="destinations">Destinations</MenuItem>
                <MenuItem value="hotels">Hotels & Accommodations</MenuItem>
                <MenuItem value="transportation">Transportation</MenuItem>
                <MenuItem value="automotive">Automotive</MenuItem>
                <MenuItem value="aviation">Aviation</MenuItem>
                
                <ListSubheader>Shopping & Retail</ListSubheader>
                <MenuItem value="shopping">Shopping</MenuItem>
                <MenuItem value="ecommerce">E-commerce</MenuItem>
                <MenuItem value="consumer_goods">Consumer Goods</MenuItem>
                <MenuItem value="luxury">Luxury Items</MenuItem>
                <MenuItem value="discounts">Discounts & Deals</MenuItem>
                
                <ListSubheader>Real Estate & Property</ListSubheader>
                <MenuItem value="real_estate">Real Estate</MenuItem>
                <MenuItem value="residential">Residential Property</MenuItem>
                <MenuItem value="commercial">Commercial Property</MenuItem>
                <MenuItem value="rental">Rental Property</MenuItem>
                <MenuItem value="mortgage">Mortgages & Loans</MenuItem>
                
                <ListSubheader>Education & Reference</ListSubheader>
                <MenuItem value="education">Education</MenuItem>
                <MenuItem value="schools">Schools & Universities</MenuItem>
                <MenuItem value="online_learning">Online Learning</MenuItem>
                <MenuItem value="reference">Reference</MenuItem>
                <MenuItem value="science">Science</MenuItem>
                <MenuItem value="history">History</MenuItem>
                
                <ListSubheader>Sports & Recreation</ListSubheader>
                <MenuItem value="sports">Sports</MenuItem>
                <MenuItem value="outdoor">Outdoor Activities</MenuItem>
                <MenuItem value="fitness_activities">Fitness Activities</MenuItem>
                <MenuItem value="team_sports">Team Sports</MenuItem>
                <MenuItem value="individual_sports">Individual Sports</MenuItem>
                
                <ListSubheader>News & Current Events</ListSubheader>
                <MenuItem value="news">News</MenuItem>
                <MenuItem value="politics">Politics</MenuItem>
                <MenuItem value="weather">Weather</MenuItem>
                <MenuItem value="world_news">World News</MenuItem>
                <MenuItem value="local_news">Local News</MenuItem>
                
                <ListSubheader>Other Categories</ListSubheader>
                <MenuItem value="arts">Arts & Culture</MenuItem>
                <MenuItem value="religion">Religion & Spirituality</MenuItem>
                <MenuItem value="legal">Legal</MenuItem>
                <MenuItem value="environment">Environment</MenuItem>
                <MenuItem value="pets">Pets & Animals</MenuItem>
                <MenuItem value="hobbies">Hobbies & Interests</MenuItem>
                <MenuItem value="general">General</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
              >
                <MenuItem value="keyword">Keyword (A-Z)</MenuItem>
                <MenuItem value="payout_desc">Payout (High to Low)</MenuItem>
                <MenuItem value="payout_asc">Payout (Low to High)</MenuItem>
                <MenuItem value="hits_desc">Hits (High to Low)</MenuItem>
                <MenuItem value="revenue_desc">Revenue (High to Low)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={() => {
            setFilters({
              status: 'all',
              performance: 'all',
              category: 'all',
              sortBy: 'keyword',
              payoutRange: [0, 1]
            });
          }} 
          color="inherit"
        >
          Reset
        </Button>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button 
          onClick={onApplyFilters} 
          variant="contained" 
          startIcon={<FilterListIcon />}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterDialog; 